"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import { extractProperty } from "utils"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"
import { TickCircleIcon } from "./icons"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.lightTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) => props.theme.colors.faqTextColorHover};
  --p-item-bg: white;
  --p-item-bg-hover: ${(props) => props.theme.colors.port.lightBackgroundColor};

  padding-block: 60px;
  background-color: var(--p-bg);
  color: var(--p-text);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 64px;
    padding-block: 80px;
  }

  position: relative;
  overflow: hidden;
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
    gap: 10px;
  }
`

const StyledRight = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    order: -1;
    gap: 24px;
    max-width: 828px;
  }
`

const StyledText = styled.h4`
  margin: 0;
  padding: 0;

  font-size: 18px;
  font-weight: 400;
  line-height: 150%; /* 27px */
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

const StyledItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
  gap: 8px;
  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
  }
`

const StyledItemText = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

type PortRunningTextProps = {
  data?: any
}

const PortRunningText = ({ data }: PortRunningTextProps) => {
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const text = extractProperty(data, "text")
  const darkMode = extractProperty(data, "darkMode") || false
  const aligned = extractProperty(data, "textAlignment") ? "right" : "left"
  const bulletsList = extractProperty(data, "bulletsList") || []

  const items = bulletsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
  }))

  // in case data fields are empty, return null
  if (!blackTitle && !blueTitle && !text && (!items || items.length === 0)) {
    return null
  }

  return (
    <StyledSection id="port-running-text" $darkMode={darkMode}>
      <PortContainer paddingInline={20}>
        <StyledContent>
          <Box
            sx={{
              maxWidth: { md: "400px" },
            }}
          >
            <PortTitle
              normalText={blackTitle}
              highlightedText={blueTitle}
              titleSx={{
                textAlign: { xs: aligned },
                direction: aligned === "right" ? "ltr" : "rtl",
                maxWidth: { xs: "100%", md: "317px", xl: "400px" },
                fontSize: {
                  xs: "30px",
                  md: "40px",
                },
                lineHeight: {
                  xs: "145%",
                  md: "125%",
                },
              }}
            />
          </Box>
          <StyledRight>
            {text && <StyledText>{text}</StyledText>}
            {items && items.length > 0 && (
              <StyledItemList>
                {items?.map((item: any, index: number) => (
                  <StyledItem key={index}>
                    <TickCircleIcon />
                    {item.title && <StyledItemText>{item.title}</StyledItemText>}
                  </StyledItem>
                ))}
              </StyledItemList>
            )}
          </StyledRight>
        </StyledContent>
      </PortContainer>
    </StyledSection>
  )
}
export default PortRunningText
